import { useState } from 'react';
import { Lock, CheckCircle2, AlertCircle, ArrowRight } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { pwnedCount, PWNED_MESSAGE } from '../lib/pwned';
import Button from './ui/Button';
import { Input, Label, FieldHint } from './ui/Input';
import { LogoLockup } from './ui/Logo';

/**
 * Ekran „ustaw nowe hasło" po kliknięciu linku z maila resetującego (zdarzenie PASSWORD_RECOVERY).
 * Do 14.10.2026 aplikacja wysyłała mail, ale nie miała tego ekranu — link logował i nic więcej.
 */
export default function SetPasswordPage({ email, onDone, onCancel }) {
  const [p1, setP1] = useState('');
  const [p2, setP2] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (p1.length < 8) return setError('Hasło musi mieć co najmniej 8 znaków.');
    if (p1 !== p2) return setError('Hasła się różnią.');
    setLoading(true);
    if (await pwnedCount(p1) > 0) { setLoading(false); return setError(PWNED_MESSAGE); }
    const { error } = await supabase.auth.updateUser({ password: p1 });
    setLoading(false);
    if (error) return setError(error.message);
    setOk(true);
    setTimeout(onDone, 900);
  };

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-ink-950 px-6 py-8 sm:px-12">
      <LogoLockup size="md" />
      <div className="w-full max-w-[420px] mx-auto py-16 animate-fade-in">
        <h1 className="text-3xl font-bold text-ink-900 dark:text-ink-50 tracking-tighter mb-2">Ustaw nowe hasło</h1>
        <p className="text-sm text-ink-500 dark:text-ink-400 mb-8">Konto <b>{email}</b>. Po zapisaniu od razu wejdziesz do aplikacji.</p>
        {error && (
          <div className="mb-5 flex items-start gap-2.5 p-3 rounded-md bg-danger-light border border-danger/20 dark:bg-danger/15 dark:border-danger/30">
            <AlertCircle className="w-4 h-4 text-danger-dark dark:text-danger flex-shrink-0 mt-0.5" />
            <span className="text-sm text-danger-dark dark:text-danger font-medium">{error}</span>
          </div>
        )}
        {ok && (
          <div className="mb-5 flex items-start gap-2.5 p-3 rounded-md bg-success-light border border-success/20 dark:bg-success/15 dark:border-success/30">
            <CheckCircle2 className="w-4 h-4 text-success-dark dark:text-success flex-shrink-0 mt-0.5" />
            <span className="text-sm text-success-dark dark:text-success font-medium">Hasło zmienione.</span>
          </div>
        )}
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="p1">Nowe hasło</Label>
            <Input id="p1" type="password" value={p1} onChange={e => setP1(e.target.value)} required autoComplete="new-password" leftIcon={<Lock className="w-4 h-4" />} autoFocus />
            <FieldHint>Co najmniej 8 znaków.</FieldHint>
          </div>
          <div>
            <Label htmlFor="p2">Powtórz hasło</Label>
            <Input id="p2" type="password" value={p2} onChange={e => setP2(e.target.value)} required autoComplete="new-password" leftIcon={<Lock className="w-4 h-4" />} />
          </div>
          <Button type="submit" size="lg" loading={loading} rightIcon={!loading && <ArrowRight className="w-4 h-4" />} className="w-full mt-2">Zapisz hasło i wejdź</Button>
          <Button type="button" variant="ghost" size="sm" onClick={onCancel} className="w-full">Anuluj i wyloguj</Button>
        </form>
      </div>
    </div>
  );
}
